import React from "react";
import Card from "components/card";
import { MdLock, MdLockOpen, MdErrorOutline } from "react-icons/md";

const TradingViewAccess = ({ accessData, isLoading, error, showHeader = false }) => {
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  // Loading state
  if (isLoading) {
    return (
      <div className={`${!showHeader ? 'h-full' : ''}`}>
        {showHeader && (
          <div className="mb-4 w-full">
            <h4 className="text-xl font-bold text-navy-700 dark:text-white">
              TradingView Access
            </h4>
          </div>
        )}
        <div className="animate-pulse flex flex-col gap-4">
          <div className="h-6 bg-navy-700 rounded w-3/4 mx-auto"></div>
          <div className="h-4 bg-navy-700 rounded w-full mx-auto"></div>
          <div className="h-40 bg-navy-700 rounded w-full mx-auto"></div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`${!showHeader ? 'h-full' : ''} text-center`}>
        {showHeader && (
          <div className="mb-4 w-full">
            <h4 className="text-xl font-bold text-navy-700 dark:text-white">
              TradingView Access
            </h4>
          </div>
        )}
        <div className="text-red-400 flex flex-col items-center">
          <MdErrorOutline size={40} className="mb-2" />
          <div>Error loading TradingView access data</div>
          <button 
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded" 
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${!showHeader ? 'h-full' : ''}`}>
      {showHeader && (
        <div className="mb-4 w-full">
          <h4 className="text-xl font-bold text-navy-700 dark:text-white">
            TradingView Access
          </h4>
          <p className="mt-2 text-base text-gray-600 dark:text-gray-400">
            {accessData?.tvUsername ? (
              <>Your TradingView username: <span className="font-semibold dark:text-white">{accessData.tvUsername}</span></>
            ) : (
              "Your TradingView script access"
            )}
          </p>
        </div>
      )}

      {accessData?.scripts && accessData.scripts.length > 0 ? (
        <div className="space-y-4">
          {accessData.scripts.map((script, index) => (
            <div 
              key={script.pine_id || index} 
              className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white/5 backdrop-blur-md"
            >
              <div className="flex justify-between items-start">
                <div>
                  <h5 className="font-medium text-navy-700 dark:text-white">
                    {script.scriptName}
                  </h5>
                  {script.description && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{script.description}</p>
                  )}
                </div>
                <div className="flex-shrink-0">
                  {script.hasAccess ? (
                    <span className="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-800 dark:bg-green-900 dark:text-green-300">
                      <MdLockOpen className="mr-1" />
                      Access Granted
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-red-100 px-3 py-1 text-sm font-medium text-red-800 dark:bg-red-900 dark:text-red-300">
                      <MdLock className="mr-1" />
                      No Access
                    </span>
                  )}
                </div>
              </div>
              
              {script.hasAccess && (
                <div className="mt-3 text-sm text-gray-600 dark:text-gray-400">
                  {script.noExpiration ? (
                    <span className="font-medium text-green-600 dark:text-green-400">Lifetime Access</span>
                  ) : (
                    <>
                      <span className="font-medium">Expires:</span> {formatDate(script.expirationDate)}
                    </>
                  )}
                </div>
              )}
              
              {script.error && (
                <div className="mt-2 text-sm text-red-500">
                  Error: {script.error}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center p-6 border border-gray-200 dark:border-gray-700 rounded-lg">
          <MdLock className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" />
          <p className="mt-2 text-gray-500 dark:text-gray-400">
            {accessData?.scripts?.length === 0 
              ? "You don't have access to any TradingView scripts"
              : "Unable to retrieve script access information"
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default TradingViewAccess;
